import {
  ActionReducerMapBuilder,
  createAction,
  createAsyncThunk,
  EntityState,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';
import { FORM_ERROR } from 'final-form';

import { AppError } from '@vk-hr-tek/core/error';
import { showCustomNotification } from '@vk-hr-tek/core/notifications';

import {
  CompanyEventsStat,
  CompanyGroupUsers,
  GetCompanyGroupListResponse,
  GetUnitsTreeParams,
  GetUnitsTreeQuery,
  GetUnitsTreeResponse,
  SettingsService,
} from '@app/gen/settings';
import { ThunkExtra } from '@app/store';
import {
  CreateVacationScheduleResponse,
  VacationAdminCompaniesListResponse,
  VacationsService,
} from '@app/gen/vacations';
import { Entity } from '@app/types';

import { VacationCreateMapper } from '../../../mappers';
import { SettingsState, SettingsWithRootState } from '../../../slice';
import {
  CreateVacationDto,
  CreateVacationCoordinationDto,
  CreateVacationMainDto,
  CreateVacationFillingDto,
} from '../../../dto';
import { SettingsVacationsRouter } from '../../../types';

export const setVacationCreateStage = createAction<
  'main' | 'filling' | 'coordination'
>('vacationCreate/setVacationCreateStage');

export const setVacationCreateFormValues = createAction<
  | CreateVacationCoordinationDto
  | CreateVacationMainDto
  | CreateVacationFillingDto
  | null
>('vacationCreate/setVacationCreateFormValues');

export const getAdminCompanies = createAsyncThunk<
  VacationAdminCompaniesListResponse,
  undefined,
  ThunkExtra
>(
  'vacationCreate/getAdminCompanies',
  async (_, { rejectWithValue, extra: { inject } }) => {
    try {
      const vacationsService = inject(VacationsService);
      const result = await vacationsService.getVacationAdminCompaniesList();

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getCompaniesGroupList = createAsyncThunk<
  GetCompanyGroupListResponse,
  { companyIds: string[] },
  ThunkExtra
>(
  'vacationCreate/getCompaniesGroupList',
  async (getCompaniesGroupListDto, { rejectWithValue, extra: { inject } }) => {
    try {
      const settingsService = inject(SettingsService);
      const result = await settingsService.getCompanyGroupList({
        body: { company_ids: getCompaniesGroupListDto.companyIds },
      });

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const createSchedule = createAsyncThunk<
  CreateVacationScheduleResponse,
  {
    companyId: string;
    actions: {
      resolve: (value: unknown) => void;
      reject: (value: unknown) => void;
    };
  },
  ThunkExtra<SettingsWithRootState>
>(
  'vacationCreate/createSchedule',
  async (
    { companyId, actions },
    { rejectWithValue, getState, dispatch, extra: { inject } },
  ) => {
    try {
      const router = inject<SettingsVacationsRouter>(SettingsVacationsRouter);
      const formValues = getState().settings.vacationCreate.formValues;
      const vacationsService = inject(VacationsService);
      const vacationCreateMapper = inject(VacationCreateMapper);

      const requestBody = vacationCreateMapper.transform(
        formValues as CreateVacationDto,
      );

      const result = await vacationsService.vacationSchedule({
        body: requestBody,
      });

      dispatch(setVacationCreateStage('main'));
      dispatch(setVacationCreateFormValues(null));

      const currentCreatedVacationId = result.items.find(
        ({ company_id }) => companyId === company_id,
      )?.id;

      setTimeout(() => {
        router.goToList(
          companyId
            ? `?company_id=${companyId}#${currentCreatedVacationId}`
            : `#${currentCreatedVacationId}`,
        );
      }, 0);

      if (result.success) {
        dispatch(
          showCustomNotification([
            {
              type: 'info',
              title: 'Новое планирование создано!',
              message: `Планирование (${result.success} шт)`,
            },
          ]),
        );
      } else {
        dispatch(
          showCustomNotification([
            {
              type: 'error',
              title: 'Не удалось создать планирование!',
              message: `Планирование (${result.errors} шт)`,
            },
          ]),
        );
      }

      actions.resolve(null);

      return result;
    } catch (err) {
      actions.reject({ [FORM_ERROR]: err });
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getUnitsTree = createAsyncThunk<
  GetUnitsTreeResponse,
  {
    query?: GetUnitsTreeQuery;
    params: GetUnitsTreeParams;
  },
  ThunkExtra
>(
  'createVacation/getUnitTree',
  async (dto, { rejectWithValue, extra: { inject } }) => {
    try {
      const unitService = inject(SettingsService);

      return await unitService.getUnitsTree(dto);
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const settingsVacationsCreateReducer = (
  builder: ActionReducerMapBuilder<
    EntityState<Entity<CompanyEventsStat | CompanyGroupUsers>, string> &
      SettingsState
  >,
) => {
  builder.addCase(getAdminCompanies.pending, (state) => {
    state.vacationCreate.status = 'loading';
    state.vacationCreate.error = null;
  });
  builder.addCase(getAdminCompanies.fulfilled, (state, action) => {
    state.vacationCreate.status = 'complete';
    state.vacationCreate.companies = action.payload.companies;
    state.vacationCreate.error = null;
  });
  builder.addCase(getAdminCompanies.rejected, (state, { payload, error }) => {
    state.vacationCreate.status = 'failed';
    state.vacationCreate.error =
      payload ||
      ({
        info:
          /* istanbul ignore next */ (error && error.message) ||
          'Unknown error',
        status: 500,
        source: 'client',
        title: 'Internal client error',
      } as AppError);
  });

  builder.addCase(getCompaniesGroupList.fulfilled, (state, action) => {
    state.vacationCreate.companyGroupList = action.payload.items;
    state.vacationCreate.error = null;
  });

  builder.addCase(
    getCompaniesGroupList.rejected,
    (state, { payload, error }) => {
      state.vacationCreate.status = 'failed';
      state.vacationCreate.error =
        payload ||
        ({
          info:
            /* istanbul ignore next */ (error && error.message) ||
            'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);
    },
  );

  builder.addCase(setVacationCreateStage, (state, action) => {
    state.vacationCreate.step = action.payload;
  });

  builder.addCase(setVacationCreateFormValues, (state, action) => {
    if (action.payload) {
      state.vacationCreate.formValues = {
        ...state.vacationCreate.formValues,
        ...(action.payload as CreateVacationDto),
      };
    } else {
      state.vacationCreate.formValues = action.payload;
    }
  });

  builder.addCase(getUnitsTree.pending, (state) => {
    if (!state.vacationCreate.unitsTree.legal.root_unit) {
      state.vacationCreate.unitsTree.legal = {};
    }

    if (!state.vacationCreate.unitsTree.operational.root_unit) {
      state.vacationCreate.unitsTree.operational = {};
    }
  });
  builder.addCase(getUnitsTree.fulfilled, (state, { payload, meta }) => {
    if (meta.arg.query?.unit_type === 'legal') {
      state.vacationCreate.unitsTree.legal = payload ?? {};
    }

    if (meta.arg.query?.unit_type === 'operational') {
      state.vacationCreate.unitsTree.operational = payload ?? {};
    }
  });
  builder.addCase(getUnitsTree.rejected, (state, { meta }) => {
    if (meta.arg.query?.unit_type === 'legal') {
      state.vacationCreate.unitsTree.legal = {};
    }

    if (meta.arg.query?.unit_type === 'operational') {
      state.vacationCreate.unitsTree.operational = {};
    }
  });
};
